// CUSTOM COMPONENTS
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import QueueMappingSidebar from "./QueueMappingSidebar";
import QueueMappingBody from "./QueueMappingBody";
import ConfirmationModal from "./ConfirmationModal";

// HOOKS
import { useManagedPositions } from "@/Common/hooks/Positions";

// TYPES
import {
	IManagedPosition,
	IPositionQueue,
	IQueue,
	IQueueMappingSidebarItem,
	IUserQueue,
	IUserWithQueue,
} from "@/Settings/types/QueueMapping/QueueMapping";

// Images
import emptyImage from "@/assets/images/commons/empty.png";

interface IQueueMappingWrapperProps {
	shouldShowEmpty: boolean;
	shouldShowDetails: boolean;
	queueLabels: string[] | null | undefined;
	queue: IQueue;
	queues: IQueue[] | null;
	selectedPositions: IManagedPosition[];
	onPositionsChange: (selected: IManagedPosition[]) => void;
	onAddQueue: () => void;
	onDeleteQueue: () => void;
	onSelectQueue: (queue: IQueueMappingSidebarItem) => void;
	handleQueueName: (title: string) => void;
	handleUpdateOverflowQueues: (overflowQueues: string[]) => void;
	handleCopyQueue: () => void;
	isLoadingQueues: boolean;
	isQueueLabelsLoading: boolean;
	associates: IUserWithQueue[] | null | undefined;
	employees: IUserWithQueue[] | null | undefined;
	isEmployeesLoading: boolean;
	isUsersLoading: boolean;
	selectedUsers: IPositionQueue[];
	onAssociatesChange: (selected: IUserWithQueue[]) => void;
	onEmployeesChange: (selected: IUserWithQueue[]) => void;
	onDeletePosition: (positionId: string) => void;
	onDeleteMembers: (usersToRemove: IPositionQueue[]) => void;
	handleSelectUser: (position: string, user: IUserQueue) => void;
	uniqueUsers: string[];
	handleConfirmModalState: (state: boolean) => void;
	confirmModalState: boolean;
	handleDeleteUnusedQueues: () => void;
}
const QueueMappingWrapper = (props: IQueueMappingWrapperProps) => {
	const {
		shouldShowEmpty,
		shouldShowDetails,
		queueLabels,
		queue,
		queues,
		selectedPositions,
		onPositionsChange,
		onAddQueue,
		onDeleteQueue,
		onSelectQueue,
		handleQueueName,
		handleUpdateOverflowQueues,
		handleCopyQueue,
		isLoadingQueues,
		isQueueLabelsLoading,
		associates,
		employees,
		isEmployeesLoading,
		isUsersLoading,
		selectedUsers,
		onAssociatesChange,
		onEmployeesChange,
		onDeletePosition,
		onDeleteMembers,
		handleSelectUser,
		uniqueUsers,
		handleConfirmModalState,
		confirmModalState,
		handleDeleteUnusedQueues,
	} = props;

	// Get managed positions
	const { data: managedPositions, isLoading: isPositionsLoading } = useManagedPositions({
		queryKeys: ["managedPositionsQueue"],
	});

	return (
		<BodyLayer
			fixedContent={
				<QueueMappingSidebar
					queueLabels={queueLabels ?? []}
					queue={queue}
					queues={queues ?? []}
					handleAddQueue={onAddQueue}
					handleDeleteQueue={onDeleteQueue}
					handleSelectQueue={onSelectQueue}
					handleCopyQueue={handleCopyQueue}
					isLoadingQueues={isLoadingQueues || isQueueLabelsLoading}
					handleConfirmModalState={handleConfirmModalState}
				/>
			}
		>
			<RoundedBox sx={{ minHeight: "630px" }} className="!p-8 !pb-12">
				{shouldShowEmpty && (
					<EmptyBody
						image={emptyImage}
						imageStyles={{ width: "340px" }}
						title={"No Queue selected."}
						bodyHeight={"340px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				)}
				{shouldShowDetails && (
					<QueueMappingBody
						isQueueLabelsLoading={isQueueLabelsLoading}
						queues={queues ?? []}
						queueLabels={queueLabels ?? []}
						managedPositions={managedPositions ?? []}
						selectedPositions={selectedPositions}
						onPositionsChange={onPositionsChange}
						queue={queue}
						handleQueueName={handleQueueName}
						handleUpdateOverflowQueues={handleUpdateOverflowQueues}
						associates={associates ?? []}
						employees={employees ?? []}
						isPositionsLoading={isPositionsLoading}
						isEmployeesLoading={isEmployeesLoading}
						isUsersLoading={isUsersLoading}
						selectedUsers={selectedUsers}
						onAssociatesChange={onAssociatesChange}
						onEmployeesChange={onEmployeesChange}
						onDeletePosition={onDeletePosition}
						onDeleteMembers={onDeleteMembers}
						handleSelectUser={handleSelectUser}
						uniqueUsers={uniqueUsers}
					/>
				)}
			</RoundedBox>
			<ConfirmationModal
				modalState={confirmModalState}
				handleConfirmModalState={handleConfirmModalState}
				handleDeleteUnusedQueues={handleDeleteUnusedQueues}
			/>
		</BodyLayer>
	);
};

export default QueueMappingWrapper;
